import { SubtitleTypography } from "@/components/common/Typographies";
import { ScrollableContainer } from "@/components/report/commons/ScrollableContainer";
import {
  TableContainer,
  TableRow,
} from "@/components/report/disease_risk/disease_risk_v1/medical_facility_report/commons";
import { ExpandMore } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import {
  Button,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  Typography,
} from "@mui/material";
import { Fragment, useState } from "react";
import { BasisTableCell, ImpactScore, IssueFromUser } from "..";
import {
  ImpactScoreResultImg,
  ReportDailyResultImg,
  RiskLabel,
  RiskType,
} from "../commons";
import YourImprovementMethodButton from "../HelpDialog/YourImprovementMethodButton";

const YOUR_JUDGEMENT_LIST = ["問題なし", "改善の余地あり", "判定不能"] as const;
export type YourJudgement = (typeof YOUR_JUDGEMENT_LIST)[number];
const CONFIG: Record<YourJudgement, RiskType> = {
  問題なし: "good",
  改善の余地あり: "alert",
  判定不能: "unknown",
} as const;

export interface YourImprovementMethodProps {
  userType: string;
  uuid: string;
  issue_from_user: IssueFromUser | null;
}
export default function YourImprovementMethod({
  issue_from_user,
}: YourImprovementMethodProps) {
  const [showingAllLifestyles, setShowingAllLifestyles] = useState(false);

  if (issue_from_user == null) {
    return <></>;
  }

  return (
    <>
      <Stack sx={{ mx: 6, mb: 30 }}>
        <Stack sx={{ mt: 12, mb: 6 }}>
          <SubtitleTypography>
            あなたが気になった睡眠課題の判定と改善方法
          </SubtitleTypography>
        </Stack>
        <Stack sx={{ gap: 4, mb: 12 }}>
          <Stack sx={{ gap: 2 }}>
            <Typography fontWeight={500} fontSize="18px">
              {issue_from_user.title}
            </Typography>
            <Divider />
          </Stack>
          {issue_from_user.judgement != null && (
            <RiskLabel
              type={CONFIG[issue_from_user.judgement]}
              title={issue_from_user.judgement}
              summary={issue_from_user.judgement_summary}
            />
          )}
          {issue_from_user.paragraphs.map((d, i) => (
            <Fragment key={i}>
              {d.description_lines?.map((line, j) => (
                <Typography
                  key={j}
                  variant="body1"
                  sx={{ whiteSpace: "pre-wrap" }}
                >
                  {line}
                </Typography>
              ))}
              {d.solution != null && (
                <Stack direction="row" justifyContent="end">
                  <YourImprovementMethodButton solution={d.solution} />
                </Stack>
              )}
            </Fragment>
          ))}
        </Stack>
        <Divider sx={{ mb: 6 }} />
        {issue_from_user.basis_table != null && (
          <>
            <Stack sx={{ gap: 4, mb: 2 }}>
              <Typography variant="subtitle1">
                {issue_from_user.basis_table.title}
              </Typography>
              <BasisTable
                item_names={issue_from_user.basis_table.item_names}
                cells={issue_from_user.basis_table.cells}
                lastRowIsAggregationResult={
                  issue_from_user.basis_table.last_row_is_aggregation_result
                }
              />
            </Stack>
            <Divider
              sx={
                issue_from_user.lifestyle_scores_table != null
                  ? {
                      border: "none",
                      borderTop: "2px dotted #E0E0E0",
                      mb: 6,
                      mt: 1, // Divider前後の余白を同量に見せるため微調整
                    }
                  : { mb: 6 }
              }
            />
          </>
        )}
        {issue_from_user.lifestyle_scores_table != null && (
          <>
            <Stack sx={{ mb: 6 }}>
              <Typography variant="subtitle1">
                {issue_from_user.lifestyle_scores_table.title}
              </Typography>

              {issue_from_user.lifestyle_scores_table
                .exists_most_impactful_lifestyle ? (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 4, mt: 2 }}
                  >
                    ※影響スコア：
                    {issue_from_user.lifestyle_scores_table.sleep_factor}
                    に悪影響を与えた可能性を評価し、スコアが55以上で悪影響あり、100に近いほど悪影響が強いことを示します。
                  </Typography>
                  <Stack alignItems="end" sx={{ pb: 4 }}>
                    <Stack direction="row" gap={1.5}>
                      <ImpactScoreResultImg type={1} />
                      <Typography
                        fontWeight={400}
                        fontSize="12px"
                        lineHeight="175%"
                        textAlign="justify"
                      >
                        生活習慣に課題があった日
                      </Typography>
                    </Stack>
                  </Stack>
                  <ImpactScoreTable
                    impact_scores={
                      showingAllLifestyles
                        ? issue_from_user.lifestyle_scores_table.impact_scores
                        : issue_from_user.lifestyle_scores_table.impact_scores.slice(
                            0,
                            1
                          )
                    }
                  />
                  {issue_from_user.lifestyle_scores_table.impact_scores.length >
                    1 &&
                    !showingAllLifestyles && (
                      <Stack direction="row" justifyContent="start">
                        <Button
                          endIcon={<ExpandMore />}
                          size="small"
                          sx={{ color: "#707070" }}
                          onClick={() => setShowingAllLifestyles(true)}
                        >
                          影響スコア2位以下を表示
                        </Button>
                      </Stack>
                    )}
                </>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      backgroundColor: "rgba(255, 58, 48, 0.08)",
                      color: "#FF3A30",
                      mb: 4,
                      mt: 4,
                      px: 4,
                      py: 3,
                    }}
                  >
                    {
                      issue_from_user.lifestyle_scores_table
                        .message_for_no_impactful_lifestyle
                    }
                  </Typography>
                  {showingAllLifestyles && (
                    <ImpactScoreTable
                      impact_scores={
                        issue_from_user.lifestyle_scores_table.impact_scores
                      }
                    />
                  )}
                  {issue_from_user.lifestyle_scores_table.impact_scores.length >
                    0 &&
                    !showingAllLifestyles && (
                      <Stack direction="row" justifyContent="start">
                        <Button
                          endIcon={<ExpandMore />}
                          size="small"
                          sx={{ color: "#707070" }}
                          onClick={() => setShowingAllLifestyles(true)}
                        >
                          生活習慣の一覧を見る
                        </Button>
                      </Stack>
                    )}
                </>
              )}
            </Stack>
            <Divider sx={{ mb: 6 }} />
          </>
        )}
      </Stack>
    </>
  );
}

function BasisTable({
  item_names,
  cells,
  lastRowIsAggregationResult,
}: {
  item_names: string[];
  cells: (BasisTableCell | null)[][];
  lastRowIsAggregationResult: boolean;
}) {
  return (
    <ScrollableContainer>
      <TableContainer
        sx={{
          minWidth: "768px",
          borderRadius: 0,
          border: "solid 1px #D8D7D6",
          mb: 4,
          ml: 6,
          mr: 6,
          "& tr th, tr td": {
            whiteSpace: "nowrap",
          },
          "& tr td": {
            px: 0,
            py: 3,
            border: "none",
            "&:nth-of-type(1)": {
              px: 4,
              borderRight: "solid 1px #D8D7D6",
            },
            "&:not(:nth-of-type(-n + 1))": {
              textAlign: "center",
            },
          },
          "& tr:last-of-type td": {
            borderTop: lastRowIsAggregationResult
              ? "3px double rgba(0, 0, 0, 0.12)"
              : null,
          },
          "& .MuiTableRow-root .MuiTableCell-head": {
            backgroundColor: "#F6F5F4",
            "& .MuiTypography-caption": {
              color: "#3D3D3D",
            },
          },
          "& tr th": {
            px: 0,
            py: 3,
            textAlign: "center",
            "& .MuiTypography-caption": {
              display: "block",
              fontSize: "11px",
            },
            "&:nth-of-type(1)": {
              borderRight: "solid 1px #D8D7D6",
              "& .MuiTypography-caption": {
                px: 4,
                textAlign: "left",
              },
              width: "244px",
            },
            "&:not(:nth-of-type(1))": {
              width: "74px",
            },
            "&:not(:nth-of-type(-n + 2))": {
              px: 0,
              "& .MuiTypography-caption": {
                borderLeft: "solid 1px #D8D7D6",
              },
            },
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="left">
                <Typography variant="caption">項目</Typography>
              </TableCell>
              {cells[0].map((_, i) => (
                <TableCell key={i}>
                  <Typography variant="caption">{i + 1}日目</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {cells.map((row, row_id) => (
              <TableRow key={row_id}>
                <TableCell>
                  <Typography fontWeight={400} fontSize="13px">
                    {item_names[row_id]}
                  </Typography>
                </TableCell>
                {row.map((x, i) => (
                  <TableCell
                    key={i}
                    sx={{
                      "& .MuiTypography-body2": {
                        fontSize: "13px",
                      },
                    }}
                  >
                    {x != null && x.type === "level" && (
                      <Typography variant="body2">
                        {x.level != null ? (
                          <ReportDailyResultImg
                            type={x.level}
                            style={{
                              margin: "auto",
                              display: "block",
                            }}
                          />
                        ) : (
                          <Typography fontWeight={400} fontSize="13px">
                            -
                          </Typography>
                        )}
                      </Typography>
                    )}
                    {x != null && x.type === "check" && (
                      <Stack direction="column" alignItems="center">
                        {x.is_checked ? (
                          <CheckIcon
                            sx={{
                              color: "#FF3A30",
                              fontSize: "13px",
                            }}
                          />
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </Stack>
                    )}
                    {x != null && x.type === "text" && (
                      <Typography
                        variant="body2"
                        sx={
                          x.is_bad
                            ? {
                                color: "#FF3A30",
                                fontWeight: 700,
                              }
                            : {}
                        }
                      >
                        {x.text}
                      </Typography>
                    )}
                    {x == null && <Typography variant="body2">-</Typography>}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ScrollableContainer>
  );
}

export function ImpactScoreTable({
  impact_scores,
}: {
  impact_scores: ImpactScore[];
}) {
  return (
    <ScrollableContainer>
      <TableContainer
        sx={{
          minWidth: "768px",
          borderRadius: 0,
          border: "solid 1px #D8D7D6",
          mb: 4,
          ml: 6,
          mr: 6,
          "& th, td": {
            whiteSpace: "nowrap",
          },
          "& tbody tr:nth-of-type(even)": {
            backgroundColor: "#FAFAFA",
          },
          "& tr:not(:last-child)": {
            borderBottom: "none",
          },
          "& tr td": {
            px: 4,
            py: 3,
            border: "none",
            "&:nth-of-type(1)": {
              textAlign: "center",
              "& .MuiTypography-root": {
                fontSize: "16px",
                fontWeight: 500,
              },
            },
            "&:nth-of-type(2)": {
              "& .MuiTypography-root": {
                fontSize: "13px",
                fontWeight: 400,
              },
            },
            "&:nth-of-type(-n + 2)": {
              px: 4,
              borderRight: "solid 1px #D8D7D6",
            },
            "&:not(:nth-of-type(-n + 2))": {
              textAlign: "center",
            },
          },
          "& .MuiTableRow-root .MuiTableCell-head": {
            backgroundColor: "#F6F5F4",
            "& .MuiTypography-caption": {
              color: "#3D3D3D",
            },
          },
          "& tr th": {
            px: 0,
            py: 3,
            textAlign: "center",
            width: "74px",
            "& .MuiTypography-caption": {
              display: "block",
              fontSize: "11px",
            },
            "&:nth-of-type(1)": {
              borderRight: "solid 1px #D8D7D6",
              textAlign: "center",
              width: "72px",
            },
            "&:nth-of-type(2)": {
              borderRight: "solid 1px #D8D7D6",
              textAlign: "left",
              width: "172px",
              "& .MuiTypography-caption": {
                px: 4,
              },
            },
            "&:not(:nth-of-type(-n + 3))": {
              "& .MuiTypography-caption": {
                borderLeft: "solid 1px #D8D7D6",
              },
            },
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="caption">影響スコア</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption">項目</Typography>
              </TableCell>
              {0 < impact_scores.length &&
                impact_scores[0].each_day_result.map((_, i) => (
                  <TableCell key={i}>
                    <Typography variant="caption">{i + 1}日目</Typography>
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {impact_scores.map((row, row_id) => (
              <TableRow key={row_id}>
                <TableCell>
                  <Typography>{row.value ?? "-"}</Typography>
                </TableCell>
                <TableCell>
                  <Typography> {row.caption}</Typography>
                </TableCell>
                {row.each_day_result.map((x, i) => (
                  <TableCell key={i}>
                    <ImpactScoreResultImg
                      type={x}
                      style={{
                        margin: "auto",
                        display: "block",
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ScrollableContainer>
  );
}
